import { useState } from 'react';
import { ShieldCheck, LockKeyhole, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

export default function AdminSecurity() {
  const { me, login } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const changePassword = async () => {
    if (form.new_password !== form.confirm) return toast.error('New passwords do not match');
    setSaving(true);
    try {
      const { data } = await api.post('/admin/access/me/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setForm({ current_password: '', new_password: '', confirm: '' });
      if (data.token) login(data.token, 'admin');
      toast.success('Password changed successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow"><ShieldCheck size={13} /> Security centre</div>
          <h1>Account security</h1>
          <p>Keep access tight. Sessions are short-lived and disabled operators are rejected immediately.</p>
        </div>
      </div>

      {me?.must_change_password && (
        <div className="admin-alert warning"><LockKeyhole size={18} /><div><strong>Password change required</strong><p>This account was issued a temporary password. Change it before doing operational work.</p></div></div>
      )}

      <div className="admin-security-grid">
        <div className="admin-card">
          <div className="admin-card-head"><div><h2>Change password</h2><p>{me?.is_owner ? 'The owner password is managed by the production secret.' : 'Use your current password to create a new one.'}</p></div></div>
          {me?.is_owner ? (
            <div className="admin-callout">Owner credentials are controlled outside the dashboard through the production secret. This prevents an operator-facing UI from becoming an escalation path.</div>
          ) : (
            <div className="space-y-4">
              <label><span>Current password</span><input type="password" autoComplete="current-password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} /></label>
              <label><span>New password</span><input type="password" autoComplete="new-password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} /></label>
              <label><span>Confirm new password</span><input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></label>
              <button className="admin-lux-button" onClick={changePassword} disabled={saving}>{saving ? 'Saving…' : 'Change password'}</button>
            </div>
          )}
        </div>

        <div className="admin-card">
          <div className="admin-card-head"><div><h2>Session posture</h2><p>Current authenticated context.</p></div></div>
          <div className="admin-security-list">
            <div><span>Identity</span><strong>{me?.display_name}</strong></div>
            <div><span>Username</span><strong>@{me?.username}</strong></div>
            <div><span>Access type</span><strong>{me?.is_owner ? 'Main access' : 'Team operator'}</strong></div>
            <div><span>Session lifetime</span><strong>8 hours</strong></div>
            <div><span>Granted modules</span><strong>{me?.is_owner ? 'All' : (me?.permissions || []).length}</strong></div>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-head"><div><h2>Recovery</h2><p>Team members receive a one-time recovery code when the owner creates or resets an account.</p></div><Clock3 size={20} className="text-slate-400" /></div>
        <div className="admin-callout">Keep the recovery code offline or in your company password manager. It is a free recovery path that does not depend on an SMS or paid email service. If it is lost, the owner can rotate the account credentials.</div>
      </div>
    </div>
  );
}
